import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Clock,
  AlertTriangle,
  Users,
  TrendingUp,
  Activity,
} from "lucide-react";
import { useRBAC } from "@/hooks/useRBAC";
import { useAuthStore } from "@/store/authSlice";
import { api } from "@/api/client";
import TrendChart from "@/components/dashboard/TrendChart";

// ── Types ─────────────────────────────────────────────────────────────────────
interface DashboardStats {
  total_documents: number;
  documents_this_month: number;
  pending_approval: number;
  expiring_soon: number;
  total_users: number;
  active_users_today: number;
  by_jenis_pnbp: Array<{ kode: string; nama: string; count: number }>;
  by_status: Array<{ kode: string; nama: string; count: number }>;
}

interface ExpiryAlert {
  document_id: string;
  judul: string;
  jenis_pnbp_nama: string;
  tanggal_berakhir: string;
  days_remaining: number;
  tingkat_kerahasiaan: string;
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({
  title,
  value,
  icon: Icon,
  color,
  subtitle,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>
          )}
        </div>
        <div className={`rounded-full p-3 ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const {
    isAdmin,
    isAuditor,
    isLeader,
    isManager,
    canViewFullStats,
    canApprove,
  } = useRBAC();

  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["dashboard", "stats"],
    queryFn: () => api.get("/dashboard/stats"),
  });

  const { data: trendData, isLoading: trendLoading } = useQuery<any>({
    queryKey: ["dashboard", "trend"],
    queryFn: () => api.get("/dashboard/trend"),
  });

  const { data: expiryAlerts = [] } = useQuery<ExpiryAlert[]>({
    queryKey: ["dashboard", "expiry-alerts"],
    queryFn: () => api.get("/dashboard/expiry-alerts", { days_ahead: 30 }),
  });

  const { data: tagCloud = [] } = useQuery({
    queryKey: ["dashboard", "tag-cloud"],
    queryFn: () => api.get("/dashboard/tag-cloud"),
  });

  const { data: recentActivity } = useQuery({
    queryKey: ["dashboard", "recent-activity"],
    queryFn: () => api.get("/dashboard/recent-activity"),
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Selamat datang, {user?.username}.
          {isAdmin && " Berikut ringkasan statistik dokumen.."}
          {isAuditor && " Anda memiliki akses baca ke semua dokumen."}
          {isLeader && " Anda dapat menyetujui dokumen."}
          {!isAdmin &&
            !isAuditor &&
            !isLeader &&
            " Berikut ringkasan statistik dokumen."}
        </p>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Dokumen"
          value={stats?.total_documents ?? 0}
          icon={FileText}
          color="bg-blue-500"
          subtitle="dokumen tersedia"
        />
        <StatCard
          title="Bulan Ini"
          value={stats?.documents_this_month ?? 0}
          icon={TrendingUp}
          color="bg-green-500"
          subtitle="dokumen baru"
        />
        {canApprove && (
          <StatCard
            title="Menunggu Persetujuan"
            value={stats?.pending_approval ?? 0}
            icon={Clock}
            color={
              (stats?.pending_approval ?? 0) > 0
                ? "bg-yellow-500"
                : "bg-gray-400"
            }
            subtitle="perlu tindakan"
          />
        )}
        <StatCard
          title="Akan Kadaluarsa"
          value={stats?.expiring_soon ?? 0}
          icon={AlertTriangle}
          color={(stats?.expiring_soon ?? 0) > 0 ? "bg-red-500" : "bg-gray-400"}
          subtitle="dalam 30 hari"
        />
        {canViewFullStats && (
          <>
            <StatCard
              title="Total Pengguna"
              value={stats?.total_users ?? 0}
              icon={Users}
              color="bg-purple-500"
            />
            <StatCard
              title="Aktif Hari Ini"
              value={stats?.active_users_today ?? 0}
              icon={Activity}
              color="bg-indigo-500"
              subtitle="pengguna login"
            />
          </>
        )}
      </div>

      {/* ── Trend Chart ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-base font-semibold text-gray-800 mb-4">
          Tren Upload Dokumen (12 Bulan Terakhir)
        </h2>
        {!trendLoading && trendData ? (
          <TrendChart data={trendData} />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
            Memuat data tren...
          </div>
        )}
      </div>

      {/* ── Bottom Row ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Expiry Alerts */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-800">
              Dokumen Akan Kadaluarsa
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {expiryAlerts.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">
                Tidak ada dokumen yang akan kadaluarsa dalam 30 hari
              </div>
            ) : (
              expiryAlerts.slice(0, 5).map((alert) => (
                <div
                  key={alert.document_id}
                  className="flex items-center gap-3 px-5 py-3"
                >
                  <div
                    className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      alert.days_remaining <= 7
                        ? "bg-red-500"
                        : alert.days_remaining <= 14
                          ? "bg-yellow-500"
                          : "bg-green-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {alert.judul}
                    </p>
                    <p className="text-xs text-gray-500">
                      {alert.jenis_pnbp_nama}
                    </p>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                      alert.days_remaining <= 7
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {alert.days_remaining} hari
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* By Jenis PNBP */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="text-base font-semibold text-gray-800">
              Dokumen per Jenis PNBP
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {(stats?.by_jenis_pnbp ?? []).map((item) => {
              const total = stats?.total_documents ?? 1;
              const pct = Math.round((item.count / total) * 100);
              return (
                <div key={item.kode} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm text-gray-700 truncate min-w-0">
                      <span className="font-mono text-xs font-semibold text-blue-600">
                        {item.kode}
                      </span>{" "}
                      {item.nama}
                    </span>
                    <span className="text-sm font-medium text-gray-900 flex-shrink-0">
                      {item.count}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {(stats?.by_jenis_pnbp ?? []).length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">
                Belum ada data
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
